if (!customElements.get('desktop-product-image-viewer')) {
  class DesktopProductImageViewer extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.activeIndex = 0;
      this.mediaElements = [];
      this.openedBy = null;
      this.syncTimeout = null;
      this.scrollFrame = null;
      this.zoomFocusFrame = null;
      this.pageScrollPosition = 0;
      this.isZoomed = false;
      this.zoomOffset = { x: 0, y: 0 };
      this.dragState = null;
      this.didDrag = false;
      this.mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

      this.handleSourceClick = this.handleSourceClick.bind(this);
      this.handleSourcePointerMove = this.handleSourcePointerMove.bind(this);
      this.handleSourcePointerOut = this.handleSourcePointerOut.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleMainScroll = this.handleMainScroll.bind(this);
      this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this);
      this.handleViewportResize = this.handleViewportResize.bind(this);
      this.handleSourceMutation = this.handleSourceMutation.bind(this);
      this.handleMainClick = this.handleMainClick.bind(this);
      this.handleMainPointerDown = this.handleMainPointerDown.bind(this);
      this.handleMainPointerMove = this.handleMainPointerMove.bind(this);
      this.handleMainPointerUp = this.handleMainPointerUp.bind(this);
      this.handleZoomWheel = this.handleZoomWheel.bind(this);
    }

    connectedCallback() {
      this.gallery = document.getElementById(this.dataset.mediaGalleryId);
      if (!this.gallery) return;

      this.render();
      this.gallery.addEventListener('click', this.handleSourceClick, true);
      this.gallery.addEventListener('pointermove', this.handleSourcePointerMove);
      this.gallery.addEventListener('pointerout', this.handleSourcePointerOut);
      this.dialog.addEventListener('click', this.handleDialogClick);
      this.main.addEventListener('click', this.handleMainClick);
      this.main.addEventListener('pointerdown', this.handleMainPointerDown);
      this.main.addEventListener('pointermove', this.handleMainPointerMove);
      this.main.addEventListener('pointerup', this.handleMainPointerUp);
      this.main.addEventListener('pointercancel', this.handleMainPointerUp);
      this.main.addEventListener('wheel', this.handleZoomWheel, { passive: false });
      this.main.addEventListener('scroll', this.handleMainScroll, { passive: true });
      window.addEventListener('resize', this.handleViewportResize);
      this.sourceObserver = new MutationObserver(this.handleSourceMutation);
      this.sourceObserver.observe(this.gallery, {
        subtree: true,
        attributes: true,
        attributeFilter: ['src', 'srcset', 'href', 'data-pswp-src', 'poster'],
      });
      this.mediaQuery.addEventListener('change', this.handleMediaQueryChange);

      if (!this.moved) {
        this.moved = true;
        document.body.appendChild(this);
      }
    }

    disconnectedCallback() {
      this.resetZoom();
      this.gallery?.removeEventListener('click', this.handleSourceClick, true);
      this.gallery?.removeEventListener(
        'pointermove',
        this.handleSourcePointerMove
      );
      this.gallery?.removeEventListener(
        'pointerout',
        this.handleSourcePointerOut
      );
      this.dialog?.removeEventListener('click', this.handleDialogClick);
      this.main?.removeEventListener('click', this.handleMainClick);
      this.main?.removeEventListener('pointerdown', this.handleMainPointerDown);
      this.main?.removeEventListener('pointermove', this.handleMainPointerMove);
      this.main?.removeEventListener('pointerup', this.handleMainPointerUp);
      this.main?.removeEventListener('pointercancel', this.handleMainPointerUp);
      this.main?.removeEventListener('wheel', this.handleZoomWheel);
      this.main?.removeEventListener('scroll', this.handleMainScroll);
      window.removeEventListener('resize', this.handleViewportResize);
      this.sourceObserver?.disconnect();
      this.mediaQuery.removeEventListener('change', this.handleMediaQueryChange);
      document.removeEventListener('keydown', this.handleKeydown);
      window.cancelAnimationFrame(this.scrollFrame);
    }

    render() {
      this.innerHTML = `
        <div class="desktop-product-image-viewer__backdrop" data-viewer-close></div>
        <section
          class="desktop-product-image-viewer__dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Product images"
          tabindex="-1"
        >
          <button
            class="desktop-product-image-viewer__close"
            type="button"
            data-viewer-close
            aria-label="Close image viewer"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 5L19 19M19 5L5 19"></path>
            </svg>
          </button>
          <div class="desktop-product-image-viewer__thumbs" data-viewer-thumbs></div>
          <div
            class="desktop-product-image-viewer__main"
            data-viewer-main
            tabindex="0"
            aria-label="Product media. Scroll vertically to view more."
          >
            <div class="desktop-product-image-viewer__track"></div>
          </div>
        </section>
      `;

      this.dialog = this.querySelector('.desktop-product-image-viewer__dialog');
      this.closeButton = this.querySelector('.desktop-product-image-viewer__close');
      this.thumbs = this.querySelector('[data-viewer-thumbs]');
      this.main = this.querySelector('[data-viewer-main]');
      this.mainTrack = this.main.querySelector(
        '.desktop-product-image-viewer__track'
      );
    }

    isDesktop() {
      return this.mediaQuery.matches;
    }

    isVisible(element) {
      return Boolean(element?.getClientRects().length);
    }

    getSourceRoot() {
      const desktopGrid = this.gallery.querySelector('.main-product__media--grid');
      if (this.isVisible(desktopGrid)) return desktopGrid;

      const slider = this.gallery.querySelector('.main-product__media--slider-wrapper');
      if (this.isVisible(slider)) return slider;

      return this.gallery;
    }

    getSourceItems() {
      const sourceRoot = this.getSourceRoot();
      if (!sourceRoot) return [];

      return Array.from(
        sourceRoot.querySelectorAll(
          '.swiper-slide[data-media-type], [data-desktop-image-viewer-source]'
        )
      )
        .filter(slide => this.isVisible(slide))
        .map(slide => {
          const mediaType =
            slide.dataset.desktopImageViewerMediaType || slide.dataset.mediaType;
          if (
            mediaType !== 'image' &&
            mediaType !== 'video' &&
            mediaType !== 'external_video'
          ) {
            return null;
          }

          const mediaContainer = slide.matches(
            '[data-desktop-image-viewer-trigger]'
          )
            ? slide
            : slide.querySelector('[data-desktop-image-viewer-trigger]');
          const image = slide.matches('img') ? slide : slide.querySelector('img');
          const anchor = slide.matches(
            'a.product__gallery-toggle[data-pswp-image]'
          )
            ? slide
            : slide.querySelector(
            'a.product__gallery-toggle[data-pswp-image]'
          );

          if (mediaType === 'image') {
            const src =
            anchor?.dataset.pswpSrc ||
            anchor?.getAttribute('href') ||
            image?.currentSrc ||
            image?.getAttribute('src') ||
            '';

            if (!src) return null;

            return {
              mediaId: slide.dataset.mediaId || '',
              mediaType,
              src,
              srcset:
                anchor?.dataset.pswpSrcset || image?.getAttribute('srcset') || '',
              thumbSrc:
                mediaContainer?.dataset.desktopImageViewerPreview ||
                image?.currentSrc ||
                image?.getAttribute('src') ||
                '',
              alt:
                mediaContainer?.dataset.desktopImageViewerAlt ||
                image?.getAttribute('alt') ||
                '',
            };
          }

          const mediaSelector = mediaType === 'video' ? 'video' : 'iframe';
          const mediaElement = slide.matches(mediaSelector)
            ? slide
            : slide.querySelector(mediaSelector);
          if (!mediaElement) return null;

          return {
            mediaId:
              slide.dataset.desktopImageViewerMediaId ||
              slide.dataset.mediaId ||
              mediaContainer?.dataset.mediaId ||
              '',
            mediaType,
            mediaElement: mediaElement.cloneNode(true),
            thumbSrc:
              mediaContainer?.dataset.desktopImageViewerPreview ||
              mediaElement.getAttribute('poster') ||
              image?.currentSrc ||
              image?.getAttribute('src') ||
              '',
            alt:
              mediaContainer?.dataset.desktopImageViewerAlt ||
              image?.getAttribute('alt') ||
              mediaElement.getAttribute('title') ||
              '',
          };
        })
        .filter(Boolean);
    }

    handleSourceClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      const trigger = target?.closest(
        '[data-desktop-image-viewer-trigger]'
      );

      if (!trigger || !this.gallery.contains(trigger) || !this.isDesktop()) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.open(trigger);
    }

    getSourceMediaElement(trigger) {
      if (trigger.matches('img, video, iframe')) return trigger;

      return trigger.querySelector('img, video, iframe');
    }

    isPointerInsideMedia(trigger, event) {
      const mediaElement = this.getSourceMediaElement(trigger);
      if (!mediaElement) return false;

      const rect = mediaElement.getBoundingClientRect();
      return (
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom
      );
    }

    hideImageViewerHint(trigger = this.activeHintTrigger) {
      const hint = trigger?.querySelector('.desktop-image-viewer-hint');
      hint?.classList.remove('is-visible');

      if (trigger === this.activeHintTrigger) {
        this.activeHintTrigger = null;
      }
    }

    handleSourcePointerMove(event) {
      if (!this.isDesktop()) return;

      const target = event.target instanceof Element ? event.target : null;
      const trigger = target?.closest(
        '[data-desktop-image-viewer-trigger]'
      );

      if (
        !trigger ||
        !this.gallery.contains(trigger) ||
        !this.isPointerInsideMedia(trigger, event)
      ) {
        this.hideImageViewerHint();
        return;
      }

      if (this.activeHintTrigger && this.activeHintTrigger !== trigger) {
        this.hideImageViewerHint();
      }

      const hint = trigger.querySelector('.desktop-image-viewer-hint');
      if (!hint) return;

      const triggerRect = trigger.getBoundingClientRect();
      hint.style.setProperty(
        '--desktop-image-viewer-hint-left',
        `${event.clientX - triggerRect.left}px`
      );
      hint.style.setProperty(
        '--desktop-image-viewer-hint-top',
        `${event.clientY - triggerRect.top}px`
      );
      hint.classList.add('is-visible');
      this.activeHintTrigger = trigger;
    }

    handleSourcePointerOut(event) {
      const target = event.target instanceof Element ? event.target : null;
      const trigger = target?.closest(
        '[data-desktop-image-viewer-trigger]'
      );
      const relatedTarget =
        event.relatedTarget instanceof Element ? event.relatedTarget : null;
      const nextTrigger = relatedTarget?.closest(
        '[data-desktop-image-viewer-trigger]'
      );

      if (trigger && trigger !== nextTrigger) {
        this.hideImageViewerHint(trigger);
      }
    }

    handleDialogClick(event) {
      const closeTrigger = event.target.closest('[data-viewer-close]');
      if (closeTrigger) {
        this.close();
        return;
      }

      const thumb = event.target.closest('[data-viewer-thumb-index]');
      if (thumb) {
        this.goTo(Number(thumb.dataset.viewerThumbIndex));
      }
    }

    getMediaItemFromTarget(target) {
      const mediaItem = target?.closest(
        '.desktop-product-image-viewer__media'
      );
      if (!mediaItem || !this.main.contains(mediaItem)) return null;
      return mediaItem;
    }

    handleMainClick(event) {
      if (this.didDrag) {
        this.didDrag = false;
        return;
      }

      const mediaItem = this.getMediaItemFromTarget(event.target);
      const index = Number(mediaItem?.dataset.viewerMediaIndex);
      if (!mediaItem?.querySelector('img') || !Number.isInteger(index)) return;

      if (!this.isMediaInMainViewport(mediaItem)) {
        this.focusAndZoom(index);
        return;
      }

      this.toggleZoom(index, event.clientX, event.clientY);
    }

    isMediaInMainViewport(mediaItem) {
      const rect = mediaItem.getBoundingClientRect();
      return Math.abs(rect.top) < 2 && rect.bottom >= window.innerHeight - 2;
    }

    focusAndZoom(index) {
      const mediaItem = this.mediaElements[index];
      if (!mediaItem?.querySelector('img')) return;

      this.goTo(index);
      const deadline = performance.now() + 1200;
      const waitForFocus = () => {
        if (!this.hasAttribute('open') || this.isZoomed) return;

        if (this.isMediaInMainViewport(mediaItem)) {
          this.toggleZoom(index, window.innerWidth / 2, window.innerHeight / 2);
          return;
        }

        if (performance.now() < deadline) {
          this.zoomFocusFrame = window.requestAnimationFrame(waitForFocus);
        }
      };
      this.zoomFocusFrame = window.requestAnimationFrame(waitForFocus);
    }

    handleMainPointerDown(event) {
      const mediaItem = this.getMediaItemFromTarget(event.target);
      const index = Number(mediaItem?.dataset.viewerMediaIndex);
      if (
        !this.isZoomed ||
        index !== this.activeIndex ||
        !mediaItem?.querySelector('img')
      ) {
        return;
      }

      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        offsetX: this.zoomOffset.x,
        offsetY: this.zoomOffset.y,
      };
      this.didDrag = false;
      mediaItem.setPointerCapture?.(event.pointerId);
      mediaItem.classList.add('is-dragging');
      event.preventDefault();
    }

    handleMainPointerMove(event) {
      const hoveredMedia = this.getMediaItemFromTarget(event.target);
      if (hoveredMedia?.querySelector('img') && !this.isZoomed) {
        this.updateZoomCursor(hoveredMedia, event.clientX, event.clientY);
      }

      if (!this.dragState || event.pointerId !== this.dragState.pointerId) {
        return;
      }

      const mediaItem = this.mediaElements[this.activeIndex];
      const image = mediaItem?.querySelector('img');
      if (!mediaItem || !image) return;

      const deltaX = event.clientX - this.dragState.startX;
      const deltaY = event.clientY - this.dragState.startY;
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) this.didDrag = true;

      this.zoomOffset = {
        x: this.dragState.offsetX + deltaX,
        y: this.dragState.offsetY + deltaY,
      };
      this.zoomOffset = this.clampZoomOffset(mediaItem, image, this.zoomOffset);
      this.updateZoomTransform(mediaItem);
      this.updateZoomCursor(mediaItem, event.clientX, event.clientY);
      event.preventDefault();
    }

    handleMainPointerUp(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) {
        return;
      }

      const mediaItem = this.mediaElements[this.activeIndex];
      mediaItem?.releasePointerCapture?.(event.pointerId);
      mediaItem?.classList.remove('is-dragging');
      this.dragState = null;
      window.setTimeout(() => {
        this.didDrag = false;
      }, 0);
    }

    handleZoomWheel(event) {
      if (this.isZoomed) event.preventDefault();
    }

    handleKeydown(event) {
      if (!this.hasAttribute('open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (this.isZoomed) {
        event.preventDefault();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.main.scrollBy({
          top: -Math.max(window.innerHeight * 0.8, 240),
          behavior: 'smooth',
        });
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.main.scrollBy({
          top: Math.max(window.innerHeight * 0.8, 240),
          behavior: 'smooth',
        });
      }
    }

    handleMainScroll() {
      if (!this.hasAttribute('open') || this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        const nextIndex = this.getClosestMediaIndex();
        if (nextIndex !== this.activeIndex) {
          this.setActiveIndex(nextIndex);
        }
      });
    }

    getClosestMediaIndex() {
      if (!this.mediaElements.length) return 0;

      const mainRect = this.main.getBoundingClientRect();
      const viewportCenter = mainRect.top + mainRect.height / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;

      this.mediaElements.forEach((mediaElement, index) => {
        const mediaRect = mediaElement.getBoundingClientRect();
        const mediaCenter = mediaRect.top + mediaRect.height / 2;
        const distance = Math.abs(mediaCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    handleMediaQueryChange(event) {
      if (!event.matches) this.close();
    }

    handleViewportResize() {
      if (!this.hasAttribute('open')) return;

      this.main.scrollTop = 0;
    }

    handleSourceMutation() {
      if (!this.hasAttribute('open')) return;

      window.clearTimeout(this.syncTimeout);
      this.syncTimeout = window.setTimeout(() => {
        const activeMediaId = this.items[this.activeIndex]?.mediaId;
        this.items = this.getSourceItems();
        this.renderItems();
        const nextIndex = this.items.findIndex(
          item => item.mediaId === activeMediaId
        );
        this.goTo(nextIndex >= 0 ? nextIndex : 0, 'auto');
      }, 50);
    }

    open(trigger) {
      this.items = this.getSourceItems();
      if (!this.items.length) return;

      const mediaId =
        trigger.dataset.desktopImageViewerMediaId ||
        trigger.dataset.mediaId ||
        trigger.closest('[data-media-id]')?.dataset.mediaId ||
        '';
      const index = this.items.findIndex(item => item.mediaId === mediaId);

      this.openedBy = trigger;
      this.activeIndex = index >= 0 ? index : 0;
      this.pageScrollPosition = window.scrollY;
      this.resetZoom();
      this.hidden = false;
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('desktop-product-image-viewer--open');
      this.renderItems();
      this.goTo(this.activeIndex, 'auto');
      document.addEventListener('keydown', this.handleKeydown);
      trapFocus(this.dialog, this.closeButton);
    }

    close() {
      if (!this.hasAttribute('open')) return;

      this.removeAttribute('open');
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      this.resetZoom();
      document.body.classList.remove('desktop-product-image-viewer--open');
      document.removeEventListener('keydown', this.handleKeydown);
      this.pauseVideos();
      removeTrapFocus(this.openedBy);
      this.openedBy = null;
      window.requestAnimationFrame(() => {
        window.scrollTo({
          top: this.pageScrollPosition,
          left: 0,
          behavior: 'auto',
        });
      });
    }

    renderItems() {
      this.resetZoom();
      this.mainTrack.replaceChildren();
      this.thumbs.replaceChildren();
      this.mediaElements = [];

      this.items.forEach((item, index) => {
        const mediaItem = document.createElement('div');
        mediaItem.className = 'desktop-product-image-viewer__media';
        mediaItem.dataset.viewerMediaIndex = index;
        mediaItem.appendChild(this.createMediaElement(item));
        if (item.mediaType === 'image') {
          mediaItem.appendChild(this.createZoomCursor());
        }
        this.mainTrack.appendChild(mediaItem);
        this.mediaElements.push(mediaItem);

        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'desktop-product-image-viewer__thumb';
        thumb.dataset.viewerThumbIndex = index;
        thumb.dataset.mediaId = item.mediaId;
        thumb.setAttribute(
          'aria-label',
          `View ${item.mediaType === 'image' ? 'image' : 'video'} ${index + 1}`
        );

        if (item.thumbSrc) {
          const thumbImage = new Image();
          thumbImage.src = item.thumbSrc;
          thumbImage.alt = '';
          thumbImage.draggable = false;
          thumb.appendChild(thumbImage);
        } else {
          thumb.classList.add('desktop-product-image-viewer__thumb--video');
        }

        if (item.mediaType !== 'image') {
          const videoBadge = document.createElement('span');
          videoBadge.className = 'desktop-product-image-viewer__thumb-video';
          videoBadge.setAttribute('aria-hidden', 'true');
          videoBadge.innerHTML =
            '<svg viewBox="0 0 24 24" focusable="false"><path d="M8 5.5V18.5L18 12Z"></path></svg>';
          thumb.appendChild(videoBadge);
        }

        this.thumbs.appendChild(thumb);
      });
    }

    createZoomCursor() {
      const cursor = document.createElement('span');
      cursor.className = 'desktop-product-image-viewer__zoom-cursor';
      cursor.setAttribute('aria-hidden', 'true');
      cursor.innerHTML = `
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M12 5V19"></path>
          <path d="M5 12H19"></path>
        </svg>
      `;
      return cursor;
    }

    createMediaElement(item) {
      if (item.mediaType === 'image') {
        const image = new Image();
        image.src = item.src;
        image.alt = item.alt;
        image.draggable = false;
        if (item.srcset) image.srcset = item.srcset;
        return image;
      }

      const mediaElement = item.mediaElement.cloneNode(true);
      mediaElement.classList.add(
        `desktop-product-image-viewer__${item.mediaType.replace('_', '-')}`
      );

      if (item.mediaType === 'video') {
        mediaElement.autoplay = true;
        mediaElement.muted = true;
        mediaElement.loop = true;
        mediaElement.playsInline = true;
        mediaElement.controls = false;
        mediaElement.setAttribute('autoplay', '');
        mediaElement.setAttribute('muted', '');
        mediaElement.setAttribute('loop', '');
        mediaElement.setAttribute('playsinline', '');
      }

      if (item.mediaType === 'external_video') {
        mediaElement.dataset.viewerSrc = mediaElement.getAttribute('src') || '';
        mediaElement.removeAttribute('src');
      }

      return mediaElement;
    }

    goTo(index, behavior = 'smooth') {
      const nextIndex = Math.max(0, Math.min(index, this.items.length - 1));
      if (!this.items.length) return;

      const mediaElement = this.mediaElements[nextIndex];
      if (!mediaElement) return;

      this.main.scrollTo({
        top: this.main.scrollTop + mediaElement.getBoundingClientRect().top - this.main.getBoundingClientRect().top,
        behavior,
      });
      this.setActiveIndex(nextIndex);
    }

    setActiveIndex(index) {
      if (index !== this.activeIndex) this.resetZoom();
      this.activeIndex = index;
      this.thumbs
        .querySelectorAll('[data-viewer-thumb-index]')
        .forEach(thumb => {
          const isActive = Number(thumb.dataset.viewerThumbIndex) === index;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
          if (isActive) {
            this.thumbs.scrollTo({
              top: Math.max(
                0,
                thumb.offsetTop -
                  (this.thumbs.clientHeight - thumb.offsetHeight) / 2
              ),
              behavior: 'smooth',
            });
          }
        });
      this.syncVideoPlayback(index);
    }

    toggleZoom(index = this.activeIndex, clientX, clientY) {
      const media = this.mediaElements[index];
      const image = media?.querySelector('img');
      if (!media || !image) return;

      if (index !== this.activeIndex) this.setActiveIndex(index);

      this.isZoomed = !this.isZoomed;
      media.classList.toggle('is-zoomed', this.isZoomed);
      this.updateZoomScrollLock();
      this.zoomOffset = this.isZoomed
        ? this.getZoomOffsetForClick(media, image, clientX, clientY)
        : { x: 0, y: 0 };
      this.updateZoomTransform(media);
    }

    resetZoom() {
      this.isZoomed = false;
      this.zoomOffset = { x: 0, y: 0 };
      this.dragState = null;
      this.updateZoomScrollLock();
      this.mediaElements?.forEach(media => {
        media.classList.remove('is-zoomed');
        media.classList.remove('is-dragging');
        media.querySelector('img')?.style.removeProperty('transform');
      });
    }

    getZoomOffsetForClick(media, image, clientX, clientY) {
      if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) {
        return { x: 0, y: 0 };
      }

      const mediaRect = media.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      const offset = {
        x: mediaRect.left + mediaRect.width / 2 - clientX,
        y: mediaRect.top + mediaRect.height / 2 - clientY,
      };
      return this.clampZoomOffset(media, image, offset, imageRect);
    }

    clampZoomOffset(media, image, offset, imageRect = image.getBoundingClientRect()) {
      const mediaRect = media.getBoundingClientRect();
      const maxX = Math.max(0, (imageRect.width - mediaRect.width) / 2);
      const maxY = Math.max(0, (imageRect.height - mediaRect.height) / 2);
      return {
        x: Math.max(-maxX, Math.min(maxX, offset.x)),
        y: Math.max(-maxY, Math.min(maxY, offset.y)),
      };
    }

    updateZoomCursor(media, clientX, clientY) {
      const cursor = media?.querySelector('.desktop-product-image-viewer__zoom-cursor');
      if (!cursor) return;

      const rect = media.getBoundingClientRect();
      cursor.style.setProperty('--zoom-cursor-x', `${clientX - rect.left}px`);
      cursor.style.setProperty('--zoom-cursor-y', `${clientY - rect.top}px`);
    }

    updateZoomTransform(media) {
      const image = media?.querySelector('img');
      if (!image) return;

      image.style.transform = this.isZoomed
        ? `translate(${this.zoomOffset.x}px, ${this.zoomOffset.y}px)`
        : '';
    }

    updateZoomScrollLock() {
      document.body.classList.toggle(
        'desktop-product-image-viewer--zoom-locked',
        this.isZoomed
      );
    }

    syncVideoPlayback(activeIndex) {
      this.mediaElements.forEach((mediaElement, index) => {
        const video = mediaElement.querySelector('video');
        const iframe = mediaElement.querySelector('iframe');

        if (video) {
          if (index === activeIndex) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }

        if (iframe) {
          if (index === activeIndex && iframe.dataset.viewerSrc) {
            iframe.setAttribute('src', iframe.dataset.viewerSrc);
          } else if (index !== activeIndex && iframe.hasAttribute('src')) {
            iframe.removeAttribute('src');
          }
        }
      });
    }

    pauseVideos() {
      this.mediaElements.forEach(mediaElement => {
        mediaElement.querySelector('video')?.pause();
        mediaElement.querySelector('iframe')?.removeAttribute('src');
      });
    }
  }

  customElements.define(
    'desktop-product-image-viewer',
    DesktopProductImageViewer
  );
}
